import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  policyholderComplement,
  policyholderFile,
  policyholders,
  type Policyholder,
  type PolicyholderCreditCheck,
  type PolicyholderInput,
} from '@/shared/api/resources';
import type { Money } from '@/shared/api/types';

/**
 * Consulta no cadastro do tomador (ADR 0102).
 *
 * O clique em Consultar grava o tomador, ou usa o que ja existe com o CNPJ, e abre a
 * consulta. O servidor responde na hora, e a analise corre em segundo plano: a basica, a
 * Master e o motor podem levar mais de um minuto. A tela pergunta como ela esta. O
 * resultado vem sempre do servidor: a barra da janela e so visual.
 */
export const PERMISSAO_DA_CONSULTA_NO_CADASTRO = 'credito.consulta_cadastro.create';

/** De quanto em quanto tempo a tela pergunta como a analise esta. */
const INTERVALO_DO_ACOMPANHAMENTO_MS = 2000;

export type SituacaoDaConsulta =
  | { fase: 'analisando' }
  | { fase: 'aprovado'; limite: Money | null }
  | { fase: 'manual' }
  | { fase: 'erro'; mensagem: string };

// A consulta que terminou sem resultado nao continua: nada foi aprovado, e a tela nao
// promete o contrario.
const mensagemSemResultado =
  'A consulta nao foi concluida, e nada foi aprovado. Veja a situacao na ficha do tomador.';

// So aqui a analise continua: quem falhou foi a leitura do acompanhamento.
const mensagemSemAcompanhamento =
  'Nao foi possivel acompanhar a consulta. A analise continua e fica registrada no tomador.';

/** Traduz o que o servidor respondeu. So o servidor diz que aprovou. */
export function situacaoDaConsulta(
  acompanhamento: PolicyholderCreditCheck,
): SituacaoDaConsulta {
  if (acompanhamento.status === 'em_andamento') return { fase: 'analisando' };
  if (acompanhamento.status === 'concluida') {
    if (acompanhamento.result === 'aprovado') {
      return { fase: 'aprovado', limite: acompanhamento.credit_limit };
    }
    if (acompanhamento.result === 'analise_manual') return { fase: 'manual' };
  }
  return { fase: 'erro', mensagem: mensagemSemResultado };
}

/**
 * O tomador do CNPJ: o que ja existe, sem duplicar, ou o que nasce agora. O CNPJ de quem a
 * corretora nao atende nao abre nada, e a mensagem nao diz de quem ele e.
 */
export async function tomadorDoCnpj(input: PolicyholderInput): Promise<Policyholder> {
  const existente = await tomadorJaCadastrado(input.document ?? '');
  if (existente) return existente;
  try {
    return await policyholders.create(input);
  } catch (erro) {
    // Dois pedidos ao mesmo tempo: o outro gravou primeiro.
    if (erro instanceof ApiError && erro.status === 409) {
      const gravado = await tomadorJaCadastrado(input.document ?? '');
      if (gravado) return gravado;
    }
    throw erro;
  }
}

async function tomadorJaCadastrado(documento: string): Promise<Policyholder | null> {
  const conferencia = await policyholderComplement.validate(documento);
  if (!conferencia.valid) {
    throw new Error(conferencia.message || 'Confira o CNPJ informado.');
  }
  if (conferencia.policyholder) return policyholders.get(conferencia.policyholder.id);
  if (conferencia.registered) {
    throw new Error(
      'Este CNPJ ja esta cadastrado, e a consulta so vale para os tomadores da sua corretora.',
    );
  }
  return null;
}

interface Pedido {
  tomador: Policyholder | null;
  consultaId: string | null;
  erro: string | null;
}

export interface ConsultaNoCadastro {
  /** A janela esta aberta. */
  aberta: boolean;
  situacao: SituacaoDaConsulta;
  /** O tomador gravado ou encontrado, assim que existir. */
  tomador: Policyholder | null;
  /** O servidor ja respondeu como terminou, ou a consulta nem comecou. */
  terminou: boolean;
  iniciar: (input: PolicyholderInput) => void;
  /** Fecha a janela. A analise continua no servidor. */
  fechar: () => void;
}

export function useConsultaNoCadastro(): ConsultaNoCadastro {
  const queryClient = useQueryClient();
  const [pedido, setPedido] = useState<Pedido | null>(null);
  const tomadorId = pedido?.tomador?.id ?? '';
  const consultaId = pedido?.consultaId ?? '';

  const acompanhamento = useQuery({
    queryKey: ['policyholder', tomadorId, 'credit-check', consultaId],
    queryFn: ({ signal }) =>
      policyholderFile.creditCheck(tomadorId, consultaId, signal),
    enabled: consultaId !== '',
    refetchInterval: (consulta) =>
      consulta.state.data?.status === 'em_andamento'
        ? INTERVALO_DO_ACOMPANHAMENTO_MS
        : false,
  });

  const iniciar = useCallback(
    (input: PolicyholderInput) => {
      setPedido({ tomador: null, consultaId: null, erro: null });
      void (async () => {
        let tomador: Policyholder | null = null;
        try {
          tomador = await tomadorDoCnpj(input);
          setPedido({ tomador, consultaId: null, erro: null });
          void queryClient.invalidateQueries({ queryKey: ['policyholders'] });
          const aberta = await policyholderFile.startCreditCheck(tomador.id, true);
          setPedido({ tomador, consultaId: aberta.id, erro: null });
        } catch (erro) {
          setPedido({
            tomador,
            consultaId: null,
            erro: erro instanceof Error ? erro.message : mensagemSemResultado,
          });
        }
      })();
    },
    [queryClient],
  );

  const fechar = useCallback(() => {
    setPedido(null);
    void queryClient.invalidateQueries({ queryKey: ['policyholders'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
  }, [queryClient]);

  let situacao: SituacaoDaConsulta = { fase: 'analisando' };
  if (pedido?.erro) {
    situacao = { fase: 'erro', mensagem: pedido.erro };
  } else if (acompanhamento.data) {
    situacao = situacaoDaConsulta(acompanhamento.data);
  } else if (acompanhamento.isError) {
    situacao = { fase: 'erro', mensagem: mensagemSemAcompanhamento };
  }

  return {
    aberta: pedido !== null,
    situacao,
    tomador: pedido?.tomador ?? null,
    terminou: situacao.fase !== 'analisando',
    iniciar,
    fechar,
  };
}
